import React, { createContext, useContext } from 'react';

import type { ChromeTone } from './engine/types';
import './flowThemes.css';

/**
 * True inside a themed flow's frosted cards. Images use it to sit on an
 * opaque white backing, so a transparent PNG with dark text keeps the white
 * surface the Course Factory image contract promises, even when the frosted
 * glass around it shows a dark scene through.
 */
const FlowThemeSurfaceContext = createContext(false);

export const FlowThemeSurfaceProvider: React.FC<{ themed: boolean; children: React.ReactNode }> = ({
  themed,
  children,
}) => <FlowThemeSurfaceContext.Provider value={themed}>{children}</FlowThemeSurfaceContext.Provider>;

export const useIsFlowThemeSurface = () => useContext(FlowThemeSurfaceContext);

/**
 * A subtle wash under a themed flow's header band (darker behind white header
 * text, lighter behind dark text), so busy scenery behind the header never
 * swallows it. Renders nothing visible until the header has a tone.
 */
export const FlowThemeHeaderScrim: React.FC<{ tone?: ChromeTone }> = ({ tone }) => (
  <div
    className="flow-theme-header-scrim pointer-events-none fixed inset-x-0 top-0 -z-10 h-24"
    aria-hidden="true"
    data-testid="flow-theme-header-scrim"
    data-flow-chrome={tone}
  />
);

/** A frosted band behind the site navigation on pages shown over a flow theme. */
export const FlowThemeNavBand: React.FC = () => (
  <div
    className="flow-theme-nav-band pointer-events-none fixed inset-x-0 top-0 -z-10 h-28 lg:h-32"
    aria-hidden="true"
    data-testid="flow-theme-nav-band"
  />
);
